import {
  Component,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { DialogA11yDirective } from '@shared/directives/a11y';
import { withViewTransition } from '@utils/view-transition.util';

@Component({
  selector: 'app-base-sidebar',
  standalone: true,
  imports: [DialogA11yDirective],
  template: `
    <!-- Backdrop -->
    <div
      class="backdrop"
      [class.backdrop--closing]="isClosing()"
      (click)="onBackdropClick()"
      aria-hidden="true"
    ></div>

    <!-- Sidebar -->
    <div
      class="sidebar-container"
      [class.sidebar-container--closing]="isClosing()"
      appDialogA11y
      [dismissible]="true"
      (escapePressed)="onEscapeKeydown()"
      role="dialog"
      aria-modal="true"
      [attr.aria-labelledby]="titleId()"
    >
      <!-- Header -->
      <header class="sidebar-header">
        <h2 [id]="titleId()" class="mat-headline-medium">
          {{ title() }}
        </h2>
        <button
          type="button"
          class="close-button"
          [attr.aria-label]="closeAriaLabel()"
          (click)="onCloseClick()"
        >
          <span class="material-symbols-outlined" aria-hidden="true"
            >close</span
          >
        </button>
      </header>

      <!-- Content -->
      <div
        class="sidebar-content"
        [class.content-padding]="addContentPadding()"
        [class.sidebar-content--closing]="isClosing()"
      >
        <ng-content />
      </div>

      <!-- Footer -->
      @if (showFooter()) {
        <footer class="sidebar-footer">
          <ng-content select="[slot=footer]" />
        </footer>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 1000;
        isolation: isolate;
        view-transition-name: sidebar-base;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.32);
        z-index: 0;
        animation: backdrop-fade-in var(--md-sys-motion-duration-short4)
          ease-out;
        view-transition-name: sidebar-backdrop;

        @starting-style {
          opacity: 0;
        }
      }

      .backdrop--closing {
        opacity: 0;
        transition: opacity var(--md-sys-motion-duration-short4) ease-in;
      }

      .sidebar-container {
        position: fixed;
        top: 0;
        right: 0;
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        box-shadow:
          0 20px 25px -5px rgb(0 0 0 / 0.1),
          0 8px 10px -6px rgb(0 0 0 / 0.1);
        background: var(--md-sys-color-surface-container-low);
        z-index: 1;
        animation: sidebar-slide-in var(--md-comp-sidebar-motion-enter);
        view-transition-name: sidebar-container;

        @starting-style {
          transform: translateX(100%);
        }
      }

      @media (min-width: 640px) {
        .sidebar-container {
          width: 24rem;
          max-width: 90vw;
        }
      }

      @media (min-width: 768px) {
        .sidebar-container {
          width: 28rem;
        }
      }

      .sidebar-container--closing {
        transform: translateX(100%);
        transition: transform var(--md-comp-sidebar-motion-exit);
      }

      .sidebar-content {
        flex: 1 1 0%;
        overflow-y: auto;
        animation: content-fade-in var(--md-sys-motion-duration-medium4)
          var(--md-sys-motion-duration-short2)
          var(--md-sys-motion-easing-emphasized) both;

        @starting-style {
          opacity: 0;
          transform: translateY(16px);
        }
      }

      .sidebar-content--closing {
        opacity: 0;
        transform: translateY(16px);
        transition:
          opacity var(--md-sys-motion-duration-short3) ease-in,
          transform var(--md-sys-motion-duration-short3) ease-in;
      }

      .sidebar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        height: 4rem;
        background: var(--md-sys-color-surface-container);
        border-bottom: 1px solid var(--md-sys-color-outline-variant);
        padding: 0 1.5rem;
      }

      .sidebar-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--md-sys-spacing-medium);
        flex-shrink: 0;
        background: var(--md-sys-color-surface-container);
        border-top: 1px solid var(--md-sys-color-outline-variant);
        padding: 1rem 1.5rem;
        min-height: 4rem;
      }

      .content-padding {
        padding: 1.5rem;
      }

      .close-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        background: transparent;
        color: var(--md-sys-color-on-surface);
        cursor: pointer;
        border-radius: 50%;
        transition: all var(--md-sys-motion-standard-effects-fast);
        position: relative;

        &:hover {
          background: rgba(from var(--md-sys-color-on-surface) r g b / 0.08);
        }

        &:focus-visible {
          outline: 2px solid var(--md-sys-color-secondary);
          outline-offset: 2px;
        }

        .material-symbols-outlined {
          font-size: 24px;
          font-variation-settings:
            'FILL' 0,
            'wght' 400,
            'GRAD' 0,
            'opsz' 24;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .backdrop,
        .sidebar-container,
        .sidebar-content {
          animation: none;
        }

        .backdrop--closing,
        .sidebar-container--closing,
        .sidebar-content--closing {
          transition: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseSidebarComponent {
  title = input.required<string>();
  closeAriaLabel = input.required<string>();
  titleId = input<string>('sidebar-title');
  addContentPadding = input<boolean>(true);
  showFooter = input<boolean>(true);

  closed = output<void>();

  isClosing = signal(false);

  onCloseClick(): void {
    this.closed.emit();
  }

  onBackdropClick(): void {
    this.closed.emit();
  }

  onEscapeKeydown(): void {
    this.closed.emit();
  }

  async startClosingAnimation(): Promise<void> {
    this.isClosing.set(true);
    await withViewTransition(() => {
      // Animation will be triggered by CSS classes
    });
  }
}
